/**
 * Swatch pictures kept between opens, so a palette shows at once instead of waiting for the swatch
 * atlas (a hidden WebGPU canvas whose first render builds every family's shaders).
 *
 * Each picture is the exact pixels a `SwatchImage` canvas received from the atlas, keyed by what
 * they depend on: the swatch (`swatchKey`), the canvas and atlas cell sizes, and
 * `SWATCH_IMAGE_VERSION`. Pictures live in memory (instant on in-app navigation) and in IndexedDB
 * (instant on a reload; all of them are read once at startup). They are only a head start: the atlas
 * still draws every swatch and repaints the canvas, so a stale picture lasts until it does. Bump the
 * version when swatches change on purpose (lighting, framing, materials).
 */

export const SWATCH_IMAGE_VERSION = 1

const DB_NAME = 'huebo:swatch-images'
const STORE = 'images'
/** Oldest pictures beyond this many are dropped (a picture is 5-20 KB). */
const MAX_ENTRIES = 400

type Record = { key: string; blob: Blob; at: number }

const memory = new Map<string, ImageBitmap>()
const saving = new Set<string>()
let loaded = false
const loadedListeners = new Set<() => void>()

export function swatchImageKey(swatch: string, px: number, cellPx: number): string {
  return `${SWATCH_IMAGE_VERSION}|${px}|${cellPx}|${swatch}`
}

export function cachedSwatchImage(key: string): ImageBitmap | undefined {
  return memory.get(key)
}

/** Calls `fn` once the stored pictures are in memory (now, if they are). Returns an unsubscribe. */
export function whenSwatchImagesLoaded(fn: () => void): () => void {
  if (loaded) {
    fn()
    return () => {}
  }
  loadedListeners.add(fn)
  return () => loadedListeners.delete(fn)
}

/** Keeps what `canvas` shows now as the picture for `key` (once per key and session). */
export function saveSwatchImage(key: string, canvas: HTMLCanvasElement): void {
  if (saving.has(key) || typeof createImageBitmap !== 'function') return
  saving.add(key)
  // Both snapshot the canvas now; encoding happens off the main thread.
  void createImageBitmap(canvas).then((bitmap) => {
    memory.get(key)?.close()
    memory.set(key, bitmap)
  }).catch(() => {})
  if (!canUseIndexedDb()) return
  canvas.toBlob((blob) => {
    if (!blob) return
    void put({ key, blob, at: Date.now() }).catch(() => {})
  }, 'image/png')
}

let dbPromise: Promise<IDBDatabase> | null = null

function canUseIndexedDb(): boolean {
  return typeof indexedDB !== 'undefined' && typeof Blob !== 'undefined'
}

function openDb(): Promise<IDBDatabase> {
  dbPromise ??= new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'key' })
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('Unable to open swatch image IndexedDB'))
    request.onblocked = () => reject(new Error('Swatch image IndexedDB upgrade blocked'))
  })
  return dbPromise
}

async function put(record: Record): Promise<void> {
  const db = await openDb()
  const tx = db.transaction(STORE, 'readwrite')
  tx.objectStore(STORE).put(record)
  await done(tx)
}

function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'))
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'))
  })
}

/** Reads every stored picture of the current version into memory; drops old versions and extras. */
async function loadAll(): Promise<void> {
  const db = await openDb()
  const tx = db.transaction(STORE, 'readwrite')
  const store = tx.objectStore(STORE)
  const records = await new Promise<Record[]>((resolve, reject) => {
    const request = store.getAll()
    request.onsuccess = () => resolve(request.result as Record[])
    request.onerror = () => reject(request.error ?? new Error('IndexedDB getAll failed'))
  })
  const prefix = `${SWATCH_IMAGE_VERSION}|`
  const current = records.filter((r) => r.key.startsWith(prefix)).sort((a, b) => b.at - a.at)
  for (const r of records) if (!r.key.startsWith(prefix)) store.delete(r.key)
  for (const r of current.slice(MAX_ENTRIES)) store.delete(r.key)
  const kept = current.slice(0, MAX_ENTRIES)
  await done(tx)
  const bitmaps = await Promise.all(kept.map((r) => createImageBitmap(r.blob).catch(() => null)))
  kept.forEach((r, i) => {
    const bitmap = bitmaps[i]
    if (bitmap && !memory.has(r.key)) memory.set(r.key, bitmap)
  })
}

function finishLoading(): void {
  loaded = true
  for (const fn of [...loadedListeners]) fn()
  loadedListeners.clear()
}

// Start reading at import (the editor chrome imports this), well before the chrome mounts.
if (typeof window !== 'undefined' && canUseIndexedDb() && typeof createImageBitmap === 'function') {
  loadAll().catch(() => {}).finally(finishLoading)
} else {
  loaded = true
}
